<template>
  <div>
    <div
      class="file_form pa-3"
      :class="classes"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
    >
      <div
        :class="{ 'text--disabled': disabled }"
        class="d-flex flex-column flex-md-row justify-space-between"
      >
        <div>
          <div class="font-weight-bold">{{ label }}</div>
          <div v-html="hint"></div>
        </div>

        <div v-if="!disabled" class="py-0">
          <v-btn class="mt-3 mt-md-0" small text @click="addFile">
            + {{ $t('common.addFile') }}
          </v-btn>
        </div>
      </div>

      <input
        v-if="!disabled"
        ref="file"
        type="file"
        class="input-file"
        :multiple="multiple"
        :accept="accept"
        @change="onChange"
      />

      <div v-cloak>
        <template v-for="(file, index) in prevValue">
          <file-upload-document-item :key="`link_${index}`" :file="file" type="link" />
        </template>

        <template v-for="(file, index) in fileList">
          <file-upload-document-item
            :key="`file_${index}`"
            :file="file"
            type="file"
            @remove="removeSentData(index)"
          />
        </template>

        <template v-for="(file, index) in invalidFileList">
          <file-upload-document-item
            :key="`valid_${index}`"
            :file="file"
            type="validate"
            @remove="remove(notValidate, index)"
          />
        </template>
      </div>
    </div>

    <base-error-message v-if="isValidateError" :errors="validateError" class="docs-error-message" />

    <base-error-message v-if="isError" :errors="errorMessages" class="docs-error-message" />
  </div>
</template>

<script>
import FileUploadDocumentItem from '@/components/fields/helpers/FileUploadDocumentItem.vue';
import BaseErrorMessage from '@/components/fields/helpers/BaseErrorMessage.vue';
import { getExtensionsByMimes, getHeadersByMimes, EXPORT_FORMATS } from '@/helpers/mimes';

export default {
  props: {
    value: null,
    prevValue: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
    },
    hint: {
      type: String,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    types: {
      type: String,
      default: 'application/pdf|image/jpeg|image/png',
    },
    maxSize: {
      type: [String, Number],
      default: 15,
    },
    sumSize: {
      type: [String, Number],
      default: 50,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    errorMessages: null,
  },
  data() {
    return {
      fileList: [],
      sentData: [],
      notValidate: [],
      isActive: false,
      loading: false,
    };
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        if (!value) {
          this.fileList = [];
          this.sentData = [];
        }
      },
    },
    sentData: {
      deep: true,
      handler(value) {
        this.$emit('input', value);
      },
    },
  },
  computed: {
    validTypes() {
      return this.types.split('|');
    },
    validHeaders() {
      return getHeadersByMimes(this.validTypes, EXPORT_FORMATS.ARRAY);
    },
    validExtensions() {
      return getExtensionsByMimes(this.validTypes);
    },
    accept() {
      if (!this.types) {
        return '*';
      }
      return this.validTypes.join(',');
    },
    isError() {
      return this.errorMessages.length > 0;
    },
    isValidateError() {
      return this.validateError.length > 0;
    },
    classes() {
      return {
        active: this.isActive,
        'error-validation': this.isError || this.isValidateError,
        disabled: this.disabled,
      };
    },
    validateError() {
      const validation = {
        type: this.$t('validation.type', { value: this.validExtensions }),
        size: this.$t('validation.size', { value: this.maxSize }),
        sumSize: this.$t('validation.sumSize', { value: this.sumSize }),
      };

      return Object.entries(this.validateErrors)
        .map(([key, value]) => {
          if (value) {
            return validation[key];
          }

          return false;
        })
        .filter(Boolean);
    },
    validateErrors() {
      const res = this.notValidate.reduce(
        (acc, { reason }) => {
          const errors = { ...acc };
          if (!reason.checkSize) {
            errors.size = true;
          }
          if (!reason.checkType) {
            errors.type = true;
          }
          return errors;
        },
        { type: false, size: false, sumSize: false }
      );

      if (!this.checkSumSize(this.fileList)) {
        res.sumSize = true;
      }

      return res;
    },
    invalidFileList() {
      return this.notValidate.map(({ file }) => file);
    },
  },
  methods: {
    async onChange() {
      const { files } = this.$refs.file;
      let fileList = [...files];

      if (!this.multiple) {
        fileList = fileList.slice(0, 1);
        this.fileList = [];
        this.sentData = [];
        this.notValidate = [];
      }

      const { validFiles, notValidFiles } = await this.checkValidation(fileList);

      this.notValidate.push(...notValidFiles);

      this.fileList.push(...validFiles);
      await this.generateData(validFiles);
    },
    checkValidation(files) {
      return files.reduce(
        async (acc, file) => {
          const checkSize = this.checkSize(file.size);
          const checkType = this.checkType(file) && (await this.checkHeader(file));

          if (checkSize && checkType) {
            acc.validFiles.push(file);
          } else {
            acc.notValidFiles.push({ file, reason: { checkSize, checkType } });
          }

          return acc;
        },
        { validFiles: [], notValidFiles: [] }
      );
    },
    checkSize(size) {
      return size <= this.mBytesToBytes(this.maxSize);
    },
    checkSumSize(files) {
      const sumSize = files.reduce((sum, file) => sum + file.size, 0);

      return sumSize <= this.mBytesToBytes(this.sumSize);
    },
    mBytesToBytes(mb) {
      return mb * 1024 * 1024;
    },
    remove(list, i) {
      list.splice(i, 1);
    },
    removeSentData(i) {
      this.remove(this.fileList, i);
      this.remove(this.sentData, i);
    },
    dragover(event) {
      event.preventDefault();
      if (this.disabled) return;
      this.isActive = true;
    },
    dragleave() {
      this.isActive = false;
    },
    drop(event) {
      event.preventDefault();

      if (this.disabled) return;

      this.isActive = false;

      const { files } = event.dataTransfer;

      if (files.length === 0) {
        return;
      }
      this.$refs.file.files = files;

      this.onChange();
    },
    addFile() {
      this.$refs.file.click();
    },
    async generateData(files) {
      this.loading = true;
      await Promise.all(
        files.map(async value => {
          const file = await this.toBase64(value);

          return {
            name: value.name,
            data: file.split(',')[1],
          };
        })
      ).then(data => {
        this.loading = false;
        this.sentData.push(...data);
      });
    },
    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    },
    async checkHeader(file) {
      try {
        const buffer = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsArrayBuffer(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = error => reject(error);
        });

        const header = new Uint8Array(buffer).subarray(0, 4).reduce((acc, curr) => {
          return acc + curr.toString(16);
        }, '');

        return this.validHeaders.includes(header);
      } catch {
        return false;
      }
    },
    checkType({ type }) {
      return this.validTypes.includes(type);
    },
  },
  components: {
    BaseErrorMessage,
    FileUploadDocumentItem,
  },
};
</script>

<style scoped lang="scss">
.file_form {
  position: relative;
  display: block;

  margin: 0 0 20px 0;
  background: var(--v-documents-base);
  border-radius: 3px;
  border: var(--v-borderDocuments-base) 2px dashed;

  &.active {
    border-style: dotted;
  }

  &.error-validation {
    border-color: var(--v-error-base);
  }

  &.disabled {
    opacity: 0.7;
  }

  .input-file {
    opacity: 0;
    position: absolute;
    bottom: 0;
    max-width: 100%;
    z-index: -1;
  }
}

.docs-error-message {
  margin-top: -15px;
}
</style>
